import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { getAllIcons, Icon as IconType } from '@lumapps/lumx/api/icons';
import { FlexBox, Orientation, Icon, Size, SelectProps, Alignment } from '@lumapps/lumx/react';
import { useTranslate, GLOBAL } from '@lumapps/translations';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { actions, reducer, initialState } from '../../ducks/slice';
import { GenericEntityPicker } from '../GenericEntityPicker';

/**
 * Displays an Icon in the context of a picker
 * @param icon - icon to display
 */
export const IconDisplay: React.FC<{ icon: IconType }> = ({ icon }) => {
    return (
        <FlexBox orientation={Orientation.horizontal} hAlign={Alignment.center}>
            <Icon icon={icon.path} size={Size.xs} />
            {/* Because of the icon height, we need to adapt the span margin */}
            <span style={{ margin: '0px' }} className="lumx-spacing-margin-left">
                {icon.name}
            </span>
        </FlexBox>
    );
};
export interface IconPickerOptions {
    /** callback to be executed when the icon selection has changed */
    onChange: (icon: IconType | null) => void;
    /** Optional flag to disable the search in the picker */
    hideSearch?: boolean;
    /** preselected icon. if null, the picker will be empty. */
    icon?: string;
    /** icon list to choose from. Disables the call to get all icons. */
    iconChoices?: IconType[];
    /** scope for tracking purposes */
    scope?: string;
    /** Optional label overwrite. Defaults to 'Pick an Icon' */
    label?: string;
    /** Other props forwarded to the Select component */
    selectProps?: Partial<SelectProps>;
}

/**
 * Component that allows to pick an icon, retrieving the list of possible icons from our CDN. Beware, when
 * rendering this component, it will trigger an API call to retrieve all icons that are "choosable". Only
 * render this component when you are absolutely sure that the next step will be selecting an icon.
 * @family Pickers
 */
export const IconPicker: React.FC<IconPickerOptions> = ({
    onChange,
    icon,
    iconChoices,
    hideSearch = false,
    scope,
    label,
    selectProps,
}) => {
    const { translateKey } = useTranslate();
    const [state, dispatch] = React.useReducer(reducer, {
        ...initialState,
        ...(iconChoices && !isEmpty(iconChoices)
            ? {
                  status: BaseLoadingStatus.idle,
                  choices: iconChoices,
              }
            : {}),
        displayedValue: icon || initialState.displayedValue,
    });

    React.useEffect(() => {
        if (state.status === BaseLoadingStatus.initial) {
            getAllIcons()
                .then((response) => {
                    dispatch(actions.onFetchSuccess(response.data));
                })
                .catch(() => {
                    dispatch(actions.onFetchFailure());
                });
        }
    });

    React.useEffect(() => {
        // Allow changing the selected choice without rerendering the component
        dispatch(actions.setSelectedChoice(icon));
    }, [icon]);

    const handleSelected = (item: IconType) => {
        dispatch(actions.setSelectedChoice(item.name));
        onChange(item);
    };

    const onClear = () => {
        dispatch(actions.unsetSelectedChoice());
        onChange(null);
    };

    return (
        <GenericEntityPicker<IconType>
            status={state.status}
            isOpen={state.isOpen}
            onInputClick={() => {
                dispatch(actions.onToggleOpen());
            }}
            label={label || translateKey(GLOBAL.PICK_ICON)}
            displayedValue={state.displayedValue || ''}
            onChange={handleSelected}
            onClear={onClear}
            choiceKeySelector={(i) => i.name}
            choices={state.choices.filter((i) => i.name.toLowerCase().indexOf(state.searchValue.toLowerCase()) > -1)}
            searchValue={state.searchValue}
            onChangeSearchValue={
                !hideSearch ? (searchValue) => dispatch(actions.onlySetSearchValue(searchValue)) : undefined
            }
            // eslint-disable-next-line react/no-unstable-nested-components
            choiceDisplayedValueSelector={(i) => {
                return <IconDisplay icon={i} />;
            }}
            // eslint-disable-next-line react/no-unstable-nested-components
            selectedValueRender={(choice: string) => {
                const i = state.choices.find((ic) => ic.name === choice);

                return i ? <IconDisplay icon={i} /> : null;
            }}
            onRetry={() => dispatch(actions.setStatus(BaseLoadingStatus.initial))}
            onDropdownClose={() => {
                dispatch(actions.onToggleOpen());
            }}
            scope={scope}
            {...selectProps}
        />
    );
};
